/**
 * pdf.js bootstrap for the takeoff viewer. Uses the pdfjs build bundled with
 * react-pdf so versions always match; the worker is the static public asset
 * (same path PdfViewer/EstimateWorkspace use) so it resolves identically in
 * dev, headless browsers, and the built PWA — and works offline after first
 * load via the service worker.
 */
import { pdfjs } from "react-pdf";
import { API, getStoredToken } from "@/lib/api";
import { fetchDrawingBytes } from "./api";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

export { pdfjs };

const docCache = new Map(); // drawingId -> Promise<PDFDocumentProxy>

/**
 * Open (or fetch from cache) a parsed PDF document.
 * `source` may be a value, a promise, or a LAZY FACTORY — factories are only
 * invoked on a cache miss, so callers never pay for a full-file download just
 * to discover the document was already open (the old `loadPdf(id, await
 * fetchDrawingBytes(id))` pattern re-downloaded the whole file for every
 * text-search page).
 */
export function loadPdf(drawingId, source) {
  if (!docCache.has(drawingId)) {
    docCache.set(drawingId, Promise.resolve()
      .then(() => (typeof source === "function" ? source() : source))
      .then((s) => pdfjs.getDocument(
        typeof s === "string" ? { url: s }
          : (s instanceof ArrayBuffer || ArrayBuffer.isView(s)) ? { data: s }
          : s
      ).promise));
  }
  return docCache.get(drawingId);
}

export function dropPdf(drawingId) {
  const p = docCache.get(drawingId);
  if (p) p.then((d) => d.destroy()).catch(() => {});
  docCache.delete(drawingId);
}

/**
 * Open a drawing as a STREAMING pdf.js document: range requests against the
 * file endpoint (which advertises Accept-Ranges) mean page 1 paints after the
 * first ~256 KB instead of after a full download — the fix for the slow
 * estimating-page loads on big plan PDFs. Falls back to the classic
 * whole-file download if streaming fails (expired token, odd proxy).
 */
export async function openPdfDoc(drawingId) {
  try {
    return await loadPdf(drawingId, () => {
      const t = getStoredToken();
      return {
        url: `${API}/takeoff/drawings/${drawingId}/file`,
        httpHeaders: t ? { Authorization: `Bearer ${t}` } : {},
        rangeChunkSize: 1048576, // 1 MB steps — 4x fewer authed round-trips
        // through the ingress than the old 256 KB steps (the ~20s page opens
        // on big plan sets were largely hundreds of small range requests).
      };
    });
  } catch (e) {
    dropPdf(drawingId);
    return loadPdf(drawingId, async () => ({ data: await fetchDrawingBytes(drawingId) }));
  }
}

/** Render a page to a canvas at a target pixel width, honouring rotation. */
export async function renderPageToCanvas(doc, pageNum, rotation, targetWidth) {
  const page = await doc.getPage(pageNum);
  const base = page.getViewport({ scale: 1 });
  const rot = ((base.rotation + (rotation || 0)) % 360 + 360) % 360;
  const rotated = page.getViewport({ scale: 1, rotation: rot });
  const scale = targetWidth / rotated.width;
  const viewport = page.getViewport({ scale, rotation: rot });
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  await page.render({ canvasContext: ctx, viewport }).promise;
  return { canvas, width: viewport.width, height: viewport.height };
}

/** Extract all text spans of a page with their display-space boxes. */
export async function pageTextSpans(doc, pageNum, rotation = 0) {
  const page = await doc.getPage(pageNum);
  const base = page.getViewport({ scale: 1 });
  const rot = ((base.rotation + (rotation || 0)) % 360 + 360) % 360;
  const viewport = page.getViewport({ scale: 1, rotation: rot });
  const tc = await page.getTextContent();
  const spans = [];
  for (const item of tc.items) {
    if (!item.str || !item.str.trim()) continue;
    const tx = item.transform;
    const [, , , , ex, ey] = tx;
    const [vx, vy] = viewport.convertToViewportPoint(ex, ey);
    const h = Math.hypot(tx[2], tx[3]);
    const w = item.width; // text-space width
    spans.push({ str: item.str, x: vx, y: vy - h, w: Math.abs(w), h });
  }
  return { spans, width: viewport.width, height: viewport.height };
}
